import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CellExportActions } from '@mode/notebooks/contract';
import { AccountFacade } from '@mode/accounts/contract';
import { CellExportFacade } from '@mode/notebooks/contract';
import { catchError, concatMap, filter, map, of, tap, withLatestFrom } from 'rxjs';
import { ToastService } from '@mode/shared/util-toast';
import { CellExportClientService } from '@mode/notebooks/data-access';
import {
  ExportDatasetErrorComponent,
  ExportDatasetProgressComponent,
  ExportDatasetSuccessComponent,
} from '@mode/notebooks/util';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';
import { DataViewFacade } from '@mode/switch-views/contract';

@Injectable({ providedIn: 'root' })
export class CellExportEffects {
  constructor(
    private actions$: Actions,
    private accountFacade: AccountFacade,
    private reportFacade: ReportFacade,
    private cellExportFacade: CellExportFacade,
    private dataViewFacade: DataViewFacade,
    private cellExportClientService: CellExportClientService,
    private toast: ToastService
  ) {}

  initExportCell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CellExportActions.initExportCell),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.cellExportFacade.notebookToken$,
        this.cellExportFacade.notebookSession$,
        this.cellExportFacade.notebookCellState$
      ),
      concatMap(([action, organization, reportToken, notebookToken, notebookSession, notebookCellState]) => {
        const { cellToken, cellContentType, datasetName } = action;
        return this.cellExportClientService
          .exportCell({
            accountToken: organization.username,
            reportToken,
            notebookToken,
            cellToken,
            payload: {
              content: notebookCellState.contents.get(cellToken),
              contentType: cellContentType,
              datasetName,
              sessionToken: notebookSession.token,
            },
          })
          .pipe(
            map((exportCellResponse) =>
              CellExportActions.initExportCellSuccess({
                cellRunToken: exportCellResponse.token,
              })
            ),
            catchError((error) => of(CellExportActions.initExportCellFailure({ error })))
          );
      })
    )
  );

  exportCellInProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CellExportActions.initExportCell),
      map(({ cellToken }) => ({
        cellToken,
        activeToast: this.toast.info<unknown, ExportDatasetProgressComponent>('', {
          disableTimeout: true,
          tapToDismiss: false,
          toastComponent: ExportDatasetProgressComponent,
        }),
      })),
      map(({ cellToken, activeToast }) =>
        CellExportActions.exportCellInProgress({
          exportingCellToken: cellToken,
          progressToastId: activeToast.toastId,
        })
      )
    )
  );

  // exportDataframeCompleted is dispatched from the legacy NotebookDataService
  // exportDataframeCompleted$ is meant to run after a cell is exported for the first time
  exportDataframeCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CellExportActions.exportDataframeCompleted),
      withLatestFrom(this.cellExportFacade.exportingCellToken$),
      filter(([action, exportingCellToken]) => {
        return !!exportingCellToken && exportingCellToken === action.payload.cell_token;
      }),
      map(([action]) => {
        const eventData = action.payload;
        if (eventData.export_successful) {
          return CellExportActions.exportCellSuccess({
            cellToken: eventData.cell_token,
            queryToken: eventData.query_token as string,
            switchViewToken: eventData.switch_view_token as string,
            localDatasetExportedAt: eventData.local_dataset_exported_at as string,
            reportRunToken: eventData.report_run_token as string,
          });
        } else {
          return CellExportActions.exportCellFailure({
            error: new Error('Failed to export dataset'),
          });
        }
      })
    )
  );

  // Loads updated DataViews after an exported dataset is run
  exportedCellRunCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CellExportActions.exportDataframeCompleted),
      withLatestFrom(this.cellExportFacade.isExporting$, this.reportFacade.headerReportToken$),
      filter(
        // to be called only if cell is not exporting and the report run is successful
        // which is after the last data run is successful
        ([action, isExporting]) => !isExporting && action.payload.export_successful && !!action.payload.report_run_token
      ),
      map(([action, _, reportToken]) =>
        CellExportActions.refreshReportRunAfterCellRun({
          reportToken,
          exportedDatasetToken: action.payload.query_token as string,
        })
      )
    )
  );

  exportedCellRunFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CellExportActions.exportDataframeCompleted),
      withLatestFrom(this.cellExportFacade.isExporting$, this.reportFacade.headerReportToken$),
      filter(
        // to be called only if cell run for an exported cell errored out
        ([action, isExporting]) => !isExporting && !action.payload.export_successful
      ),
      map(([action, _, reportToken]) =>
        CellExportActions.refreshReportRunAfterCellRun({
          reportToken,
          exportedDatasetToken: action.payload.query_token as string,
        })
      )
    )
  );

  exportCellSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CellExportActions.exportCellSuccess),
      withLatestFrom(
        this.cellExportFacade.inProgressToastId$,
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$
      ),
      map(([action, inProgressToastId, { username }, reportToken]) => {
        if (inProgressToastId) {
          this.toast.remove(inProgressToastId);
        }
        const exportedDatasetLink = `editor/${username}/reports/${reportToken}/datasets/${action.switchViewToken}`;
        this.toast.success('', {
          closeButton: true,
          disableTimeout: true,
          tapToDismiss: false,
          toastComponent: ExportDatasetSuccessComponent,
          payload: {
            exportedDatasetLink,
          },
        });

        return CellExportActions.refreshDataViewsAfterExport({
          reportToken,
          exportedDatasetToken: action.queryToken,
        });
      })
    )
  );

  exportCellFailures$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CellExportActions.initExportCellFailure, CellExportActions.exportCellFailure),
        withLatestFrom(this.cellExportFacade.inProgressToastId$),
        tap(([_, inProgressToastId]) => {
          if (inProgressToastId) {
            this.toast.remove(inProgressToastId);
          }
          this.toast.error('', {
            closeButton: true,
            disableTimeout: false,
            tapToDismiss: false,
            toastComponent: ExportDatasetErrorComponent,
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  refreshDataViewAfterCellUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CellExportActions.refreshDataViewAfterCellUpdate),
      withLatestFrom(this.reportFacade.headerReportToken$, this.dataViewFacade.enhancedDatasetResults$),
      map(([action, reportToken, enhancedDatasetResults]) => {
        const queryToken =
          enhancedDatasetResults.find((dataset) => dataset.producerToken === action.cellToken)?.queryToken || '';
        return CellExportActions.refreshReportRunAfterCellRun({
          reportToken,
          exportedDatasetToken: queryToken,
        });
      })
    )
  );
}
